import { createFileRoute, stripSearchParams } from '@tanstack/react-router';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { userProfileQuery } from '~/util/queries/public/profiles';
import { MergeRequestUserSummary } from '../../-MergeRequestUserSummary';
import { directMergeMutation } from '~/util/mutations/staff/merges';
import {
    defaultDirectMergeParams,
    directMergeParamsSchema,
} from './-SearchParams';
import { useAppForm } from '~/util/hooks/useAppForm';

export const Route = createFileRoute('/_authenticated/staff/_mergesLayout/merges/direct')({
    validateSearch: directMergeParamsSchema,
    search: {
        middlewares: [stripSearchParams(defaultDirectMergeParams)],
    },
    loaderDeps: ({ search: { primary_user, secondary_user } }) => ({
        primary_user: primary_user ? primary_user.id : null,
        secondary_user: secondary_user ? secondary_user.id : null,
    }),
    loader: async ({
        context: { queryClient },
        deps: { primary_user, secondary_user },
    }) => {
        const [
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        ] = await Promise.all([
            primary_user
                ? queryClient.fetchQuery(
                      userProfileQuery({
                          list: 'classic',
                          userid: primary_user,
                      }),
                  )
                : null,
            primary_user
                ? queryClient.fetchQuery(
                      userProfileQuery({
                          list: 'platformer',
                          userid: primary_user,
                      }),
                  )
                : null,
            secondary_user
                ? queryClient.fetchQuery(
                      userProfileQuery({
                          list: 'classic',
                          userid: secondary_user,
                      }),
                  )
                : null,
            secondary_user
                ? queryClient.fetchQuery(
                      userProfileQuery({
                          list: 'platformer',
                          userid: secondary_user,
                      }),
                  )
                : null,
        ]);
        return {
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        };
    },
    component: DirectMerge,
});

function DirectMerge() {
    const {
        primary_user_classic,
        primary_user_platformer,
        secondary_user_classic,
        secondary_user_platformer,
    } = Route.useLoaderData({
        select: ({
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        }) => ({
            primary_user_classic,
            primary_user_platformer,
            secondary_user_classic,
            secondary_user_platformer,
        }),
    });

    const navigate = Route.useNavigate();
    const search = Route.useSearch({
        select: ({ primary_user, secondary_user }) => ({
            primary_user,
            secondary_user,
        }),
    });

    const mutation = useApiMutation({
        ...directMergeMutation(),
        onSuccess: () => {
            navigate({
                search: {
                    primary_user: null,
                    secondary_user: null,
                },
            });
        },
    });

    const form = useAppForm({
        defaultValues: search,
        listeners: {
            onChange: ({ formApi }) => {
                navigate({ search: formApi.state.values });
            },
            onChangeDebounceMs: 500,
        },
        onSubmit: ({
            formApi: {
                state: {
                    values: { primary_user, secondary_user },
                },
            },
        }) => {
            if (
                primary_user &&
                secondary_user &&
                primary_user.id != secondary_user.id
            )
                mutation.mutate({
                    primary_user: primary_user!,
                    secondary_user: secondary_user!,
                });
        },
    });

    return (
        <form
            id="direct-merge"
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
            }}
        >
            <div className="flex flex-col lg:flex-row gap-4 w-full">
                <div className="w-full border rounded-lg p-4">
                    <h2>Primary User</h2>
                    <p className="mb-2">
                        This user will be kept after the merge
                    </p>
                    <form.AppField name="primary_user">
                        {(field) => (
                            <field.UserField
                                hideLabelAndErrors
                                tagOptions={{ showFullName: true }}
                                classname="w-fit"
                            />
                        )}
                    </form.AppField>
                    <MergeRequestUserSummary
                        classic={primary_user_classic}
                        platformer={primary_user_platformer}
                    />
                </div>
                <div className="w-full border rounded-lg p-4">
                    <h2>Secondary User</h2>
                    <p className="mb-2">
                        This user will have all its data merged into the primary
                        user, and will then be deleted.
                    </p>
                    <form.AppField name="secondary_user">
                        {(field) => (
                            <field.UserField
                                hideLabelAndErrors
                                tagOptions={{ showFullName: true }}
                                classname="w-fit"
                            />
                        )}
                    </form.AppField>
                    <MergeRequestUserSummary
                        classic={secondary_user_classic}
                        platformer={secondary_user_platformer}
                    />
                </div>
            </div>
            <div className="p-4 flex gap-4 justify-center">
                <form.AppForm>
                    <form.SubscribeButton label="Merge Users" />
                </form.AppForm>
            </div>
        </form>
    );
}
